'use strict';
//***********************************************************************************
//***********************************************************************************
//**** cn_svg_tool_walls : a SVG tool to build walls
//***********************************************************************************
//***********************************************************************************

import { CN_WALL_COSINE_SHAPE_THRESHOLD } from '../model/cn_model_constants';
import { CN_INNER, CN_MIDDLE, CN_OUTER, cn_reverse_axis, cn_wall } from '../model/cn_wall';
import { cn_vertex } from '../model/cn_vertex';
import { cn_add, cn_cart, cn_clone, cn_dist, cn_dot, cn_flip, cn_intersect_line, cn_mul, cn_normal, cn_normalize, cn_polar, cn_sub } from '../utils/cn_utilities';
import { cn_snap } from './cn_snap';
import { cn_pastille } from './cn_pastille';
import { cn_svg_tool_creation } from './cn_svg_tool_creation';
import { cn_polygon_handler } from './cn_polygon_handler';
import { cn_wall_type } from '../model/cn_wall_type';
import { cn_fence_type } from '../model/cn_fence_type';
import { cn_balcony_type } from '../model/cn_balcony_type';
import { cn_edition_handler } from './cn_edition_handler';
import { cn_camera, cn_element, extension_instance } from '..';
import { cn_wall_handler } from './cn_wall_handler';
import { cn_measure } from './cn_measure';
import { cn_niche } from '../model/cn_niche';
import { HELPER } from '../utils/cn_wordings';
import { cn_niche_handler } from './cn_niche_handler';

export const CN_CREATION_MODE_RECTANGLE = 0;
export const CN_CREATION_MODE_POLYLINE = 1;

//***********************************************************************************
//**** Internal calss to manage wall measures
//***********************************************************************************
class cn_tmp_wall_measure {
    constructor(parent, wall) {
        this._parent = parent;

        this._wall = wall;
        this._original_positions = [cn_clone(wall.vertices[0].position), cn_clone(wall.vertices[1].position)];
        this._max_distance = 0;

        this._lock = -1;
        this._locked_value = 0;

        this._measures = [new cn_measure(parent), new cn_measure(parent)];
        this._measures[0].set_measure = (value, d) => {
            this.set_measure(0, value)
        };
        this._measures[1].set_measure = (value, d) => {
            this.set_measure(1, value)
        };
    }

    /**
     * Sets the wall, if different from curent one
     * @param {cn_wall} wall
     * @returns
     */
    set_wall(wall) {
        if (wall == this._wall) return;
        this._wall = wall;
        this._original_positions = [cn_clone(wall.vertices[0].position), cn_clone(wall.vertices[1].position)];
    }

    /**
     *
     */
    fix_wall(max_distance) {
        this._original_positions = [cn_clone(this._wall.vertices[0].position), cn_clone(this._wall.vertices[1].position)];
        this._max_distance = max_distance;
    }

    /**
     * Sets selectable
     * @param {boolean} selectable
     */
    set_selectable(selectable) {
        this._measures.forEach(m => m.selectable = (selectable) ? 0 : -1);
    }

    /**
     * Draw method
     * @param {cn_camera} camera
     * @returns
     */
    draw(camera, selectable) {
        this._measures[0].vertices[0] = this._wall.shape[0];
        this._measures[0].vertices[1] = this._wall.shape[3];
        this._measures[1].vertices[0] = this._wall.shape[2];
        this._measures[1].vertices[1] = this._wall.shape[1];
        return this._measures[0].draw_with_highlight(camera, this._lock == 0) + this._measures[1].draw_with_highlight(camera, this._lock == 1);
    }

    clear_move() {
        this._measures.forEach(m => m.clear_move());
    }

    move(ev) {
        if (this._measures[0].move(ev)) return true;
        if (this._measures[1].move(ev)) return true;
        return false;
    }

    click(ev) {
        return this._measures.some(m => m.click(ev));
    }

    grab(ev) {
        return this._measures.some(m => m.grab(ev));
    }

    set_measure(side, value) {
        const offset = value - this._measures[side]._position[2];
        const position = cn_add(this._wall.vertices[1].position, cn_mul(this._wall.bounds.direction, offset));
        if (!this._parent.set_vertex_position(this._wall.vertices[1], position)) return;
        this._lock = side;
        this._locked_value = value;
        this._original_positions[1] = cn_clone(position);
    }
}

//***********************************************************************************
//**** cn_svg_tool_walls : a SVG tool to build walls
//***********************************************************************************
export class cn_svg_tool_wall_creation extends cn_svg_tool_creation {
    constructor(map, filters = ['wall', 'balcony'], default_mode = 0) {
        super(map);

        this._mode = default_mode;
        this._current_point = [0, 0];

        this._filters = filters;
        this._current_wall_type = null;
        if (filters.indexOf('wall') >= 0)
            this._current_wall_type = this._scene.building.get_wall_types()[0];
        if (filters.indexOf('balcony') >= 0)
            this._current_wall_type = this._scene.building.get_balcony_types()[0];
        if (filters.indexOf('fence') >= 0)
            this._current_wall_type = this._scene.building.get_fence_types()[0];
        this._current_axis = extension_instance.config.default_axis_wall;
        this.clear_selection();

        this._check_creation = new cn_pastille([0, 0], 'check_white.svg');
        this._check_creation.svg_class = 'pastille_check';

        this._undo_last_wall = new cn_pastille([0, 0], 'close_white.svg');
        this._undo_last_wall.svg_class = 'pastille_undo';

        this._mouseover_previous_point = false;
        this._grab_double_click = false;

        this._back_scene = null;

        this._split_wall_mode = false;
        this._split_position = null;
        this._split_wall = null;
        this._split_vertex = null;

        this._handler = null;

        this._visible = true;

        this.element_filter = element => {
            return element.constructor == cn_wall || element.constructor == cn_vertex
        };
        this._wall_measures = [];

        this._extra_measures = [];

        this._max_distance = 0;

        this._camera = null;

        this._niche_mode = false;
        this._niche = new cn_niche(this._building.get_niche_types()[0],this._scene);
    }

    //***********************************************************************************
    //**** Refresh transaction
    //***********************************************************************************
    transaction_refresh() {
        this.clear_selection();
    }

    is_creating() {
        if (this._handler) return this._handler.is_creating();
        return this._tmp_vertices.length > 1;
    }

    //***********************************************************************************
    //**** element types
    //***********************************************************************************
    get_element_types() {
        var lst = [];
        if (this._filters.indexOf('wall') >= 0)
            lst = lst.concat(this._scene.building.get_wall_types());
        if (this._filters.indexOf('balcony') >= 0)
            lst = lst.concat(this._scene.building.get_balcony_types());
        if (this._filters.indexOf('fence') >= 0)
            lst = lst.concat(this._scene.building.get_fence_types());
        return lst;
    }

    //***********************************************************************************
    //**** filters
    //***********************************************************************************
    set_filters(filters) {
        this._filters = filters;
        var element_types = this.get_element_types();
        var et = (element_types.length > 0) ? element_types[0] : null;
        this.set_current_wall_type(et);
    }

    //***********************************************************************************
    //**** current mode
    //***********************************************************************************
    /**
     * Returns the current creation mode. Can be :
     * - 0 : rectangle
     * - 1 : polyline
     * @returns {number}
     */
    get_mode() {
        return this._mode;
    }

    /**
     * Sets the current creation mode.
     * @param {number} mode
     */
    set_mode(mode) {
        if (mode == this._mode) return;
        this._mode = mode;
        this.clear_selection();
        this._push_instruction_by_mode();
    }

    /**
     * Sets / unsets split mode
     * @param {boolean} v
     * @returns
     */
    set_split_wall_mode(v) {
        if (this._split_wall_mode == v) return;
        this._split_wall_mode = v;
        if (v) this._niche_mode = false;
        this._push_instruction_by_mode();
        this.clear_selection();
        this._terminate_edition();
    }

    /**
     * Returns current split mode
     * @returns {boolean}
     */
    get_split_wall_mode() {
        return this._split_wall_mode;
    }

    set_niche_mode(v) {
        if (this._niche_mode == v) return;
        this._niche_mode = v;
        if (v) this._split_wall_mode = false;
        this._push_instruction_by_mode();
        this.clear_selection();
        if (v) this._terminate_edition();
    }
    
    /**
     * Returns current split mode
     * @returns {boolean}
     */
    get_niche_mode() {
        return this._niche_mode;
    }

    //***********************************************************************************
    //**** Returns current wall
    //***********************************************************************************
    get_current_wall_type() {
        return this._current_wall_type;
    }

    set_current_wall_type(wt) {
        if (this._current_wall_type == wt) return;
        this._current_wall_type = wt;
        this.clear_selection();
        this._terminate_edition();
    }

    /**
     * Checks if current wall type exists.
     * If not, will use the first wall type available.
     */
    check_current_element_type() {
        var element_types = this.get_element_types();
        if (element_types.indexOf(this._current_wall_type) >= 0) return;
        var et = (element_types.length > 0) ? element_types[0] : null;
        this.set_current_wall_type(et);
    }

    /**
     * Select elements by wall type
     * @param {cn_wall_type | cn_balcony_type | cn_fence_type} wt
     */
    select_elements_by_type(wt) {
        this._initiate_edition(this._scene.walls.filter(w => w.wall_type == wt));
    }

    //***********************************************************************************
    //**** Returns current axis
    //***********************************************************************************
    get_current_axis() {
        return this._current_axis;
    }

    set_current_axis(axis) {
        this._current_axis = axis;
        this._terminate_edition();
    }

    //***********************************************************************************
    //**** Open tool
    //***********************************************************************************
    open_tool() {
        super.open_tool();
        this.clear_selection();
        this._push_instruction_by_mode();
    }

    _push_instruction_by_mode() {
        const type = this._filters[0];
        if (this._split_wall_mode) {
            this.push_instruction_input(HELPER[type].creation.split);
        }
        else if (this._niche_mode) {
            this.push_instruction_input(HELPER[type].creation.niche);
        }
        else {
            if (this._mode === 0) {
                this.push_instruction_input(HELPER[type].creation.shape);
            } else {
                this.push_instruction_input(HELPER[type].creation.simple);
            }
        } 
    }

    close_tool() {
        this.set_niche_mode(false);
        super.close_tool();
        this.clear_selection();
    }

    //***********************************************************************************
    //**** Clear selection
    //***********************************************************************************
    clear_selection() {
        this._can_close_contour = false;
        this._mouseover_vertex = null;
        this._tmp_vertices = [new cn_vertex([0, 0], false, this._scene)];
        this._tmp_walls = [];
        this._vertex_mode = 0;
        this._snap_svg = '';
        if (this._mode == 0)
            this.initiate_rectangle();
        else
            this._handler = null;
        this._split_position = null;
    }

    //***********************************************************************************
    //**** Draws  specific svg for the tool. Returns svg string
    //***********************************************************************************
    draw(camera) {
        this._camera = camera;
        var html = super.draw(camera);

        if (!this._visible && !this.is_creating()) return html;

        //**** Special case for split mode *******/
        if (this._split_wall_mode) {
            if (this._split_position)
                html += camera.draw_scissor(this._split_position);
            return html;
        }

        //**** Special case for niche mode *******/
        if (this._niche_mode) {
            if (this._niche)
                html += this._niche.draw(camera);;
            return html;
        }

        var nb_walls = this._tmp_walls.length;
        var walls = [];
        walls = walls.concat(this._tmp_walls);

        if (this._mode != CN_CREATION_MODE_RECTANGLE) {
            if (!this._mouseover_vertex)
                nb_walls--;

            if (this._tmp_walls.length && this._mouseover_vertex && this._tmp_vertices.indexOf(this._mouseover_vertex) >= 0)
                this._tmp_walls[this._tmp_walls.length - 1].vertices[1] = this._mouseover_vertex;

            if (!this._mouseover_vertex)
                walls.splice(this._tmp_walls.length - 1, 1);
        }

        this._scene.scene_analyser.update_tmp_walls(walls);

        //*** Measures */
        while (this._wall_measures.length < nb_walls) this._wall_measures.push(new cn_tmp_wall_measure(this, this._tmp_walls[this._wall_measures.length]));
        this._wall_measures.splice(nb_walls, this._wall_measures.length - nb_walls);
        for (var ni = 0; ni < nb_walls; ni++) {
            var w = this._tmp_walls[ni];
            html += w.draw(camera);
            this._wall_measures[ni].set_wall(w);
            this._wall_measures[ni].set_selectable(this._mode == CN_CREATION_MODE_POLYLINE && ni == this._tmp_walls.length - 2)
            html += this._wall_measures[ni].draw(camera);
        }

        //*** Special measures for last point */
        if (walls.length && walls[walls.length - 1].vertices[1].support_wall) {
            const last_wall = walls[walls.length - 1];
            const last_vertex = last_wall.vertices[1];
            const segment = last_vertex.support_wall.get_side_segment(last_vertex.support_wall_side);
            const p0 = last_wall.shape[2];
            const p1 = last_wall.shape[3];
            this._extra_measures.push(new cn_measure(this, segment[0], p0, false), new cn_measure(this, p1, segment[1], false));
        }

        this._extra_measures.forEach(m => html += m.draw_with_highlight(camera));

        //*** Draw snap results */
        html += this._snap_svg;

        //*** Draw current vertex */
        if (this._mouseover_vertex) {
            var p = camera.world_to_screen(this._mouseover_vertex.position);
            html += `<circle class="move_arrow_cursor" cx="${p[0]}" cy="${p[1]}" r="3" />`;
            html += camera.draw_move_arrow(this._mouseover_vertex.position, 'move_arrow');
        }

        //*** draw angle
        if (this._tmp_walls.length > 0 && this._mode == 1) {
            for (var k = 0; k < 2; k++) {
                var vertex = this._tmp_walls[this._tmp_walls.length - 1].vertices[k];
                html += vertex.draw_angles(camera);
            }

            var classes = 'move_arrow';
            if (this._mouseover_previous_point)
                classes += ' mouseover';

            html += camera.draw_move_arrow(this._tmp_walls[this._tmp_walls.length - 1].vertex_position(0), classes);
        }

        //*** draw check pastille
        this._check_creation.visible = false;
        this._undo_last_wall.visible = false;
        if (this._tmp_walls.length > 1 && this._mode == 1) {

            const pc0 = camera.world_to_screen(this._tmp_walls[this._tmp_walls.length - 2].vertices[0].position);
            const pc1 = camera.world_to_screen(this._tmp_walls[this._tmp_walls.length - 1].vertices[0].position);
            const dir = cn_sub(pc0, pc1);
            cn_normalize(dir);

            this._check_creation.visible = true;
            this._check_creation.position = camera.screen_to_world(cn_add(pc1, cn_mul(dir, 50)));
            html += this._check_creation.draw(camera);

            this._undo_last_wall.visible = true;
            this._undo_last_wall.position = camera.screen_to_world(cn_add(pc1, cn_mul(dir, 100)));
            html += this._undo_last_wall.draw(camera);
        }

        //*** we call the handler drawing method, but we don't use it */
        if (this._handler) this._handler.draw(camera);

        //*** restore walls and vertices */
        if (this._tmp_walls.length) {
            this._tmp_walls[this._tmp_walls.length - 1].vertices[1] = this._tmp_vertices[this._tmp_vertices.length - 1];
        }

        return html;
    }

    //***********************************************************************************
    //**** Mouse callbacks
    //***********************************************************************************
    clear_move() {
        this._visible = false;
        this._mouseover_vertex = null;
        this._mouseover_previous_point = false;
        this._wall_measures.forEach(w => w.clear_move());
        this._split_position = null;
        super.clear_move();
    }

    click(ev) {
        if (super.click(ev)) return true;

        if (this._split_wall_mode) {
            this.update_split(ev);
            this.split_wall();
            return true;
        }

        if (this._niche_mode) {
            this.update_niche(ev);
            this.create_niche();
            return true;
        }

        if (this._wall_measures.some(wm => wm.click(ev)))
            return true;

        if (this._handler)
            return this._handler.drop(ev);

        //*** Maybe double cllick on last vertex ? */
        if (this._tmp_vertices.length && this._tmp_walls.length && this._grab_double_click) {
            this._can_close_contour = true;
            this.close_contour(false);
            return true;
        }

        //*** maybe end of creation by checking ?
        if (this._check_creation.visible) {
            this._check_creation.mouseover = this._check_creation.contains(ev.mouse_world, ev.camera);
            if (this._check_creation.mouseover) {
                this._can_close_contour = true;
                this.close_contour(true);
                return true;
            }
        }

        if (this._undo_last_wall.visible) {
            this._undo_last_wall.mouseover = this._undo_last_wall.contains(ev.mouse_world, ev.camera);
            if (this._undo_last_wall.mouseover) {
                this.remove_last_wall();
                this.move(ev);
                return true;
            }
        }

        return this.drop(ev);
    }

    grab(ev) {
        this._grab_double_click = false;
        if (super.grab(ev)) return true;

        if (this._split_wall_mode) return true;
        if (this._niche_mode) return true;

        if (this._wall_measures.some(wm => wm.grab(ev)))
            return true;

        if (this._handler)
            return this._handler.grab(ev);

        if (this._mouseover_previous_point) {
            this._grab_double_click = true;
            this.remove_last_wall();
            this.move(ev);
        }
        return true;
    }

    drop(ev) {
        if (super.drop(ev)) return true;

        if (this._split_wall_mode) return true;
        if (this._niche_mode) return true;

        if (this._handler)
            return this._handler.drop(ev);

        this.update_contour(ev, false);

        //*** we initiate a contour
        if (this._tmp_walls.length == 0) {
            this.initiate_contour();
        } else if (this._can_close_contour) {
            this.close_contour();
            return true;
        } else if (this._mouseover_vertex)
            this.resume_contour();

        this.update_contour(ev, false);
        return true;
    }

    move(ev) {
        this.clear_move();
        if (super.move(ev)) return true;

        if (this._split_wall_mode) {
            this.update_split(ev);
        }

        if (this._niche_mode) {
            this.update_niche(ev);
        }

        if (this._wall_measures.some(wm => wm.move(ev))) {
            return true;
        }

        this._visible = true;
        if (this._handler)
            return this._handler.move(ev);

        this.update_contour(ev, false);
        return true;
    }

    drag(ev) {
        if (super.drag(ev)) return true;

        if (this._handler)
            return this._handler.drag(ev);

        this.update_contour(ev, true);
        return true;
    }

    keydown(ev) {
        let result = super.keydown(ev);
        if (!result && (ev.key === 'Escape' || ev.key === 'Backspace')) {
            if (this._split_wall_mode) {
                this.set_split_wall_mode(false);
                this.call('end_split_mode');
                result = true;
            } else if (this._niche_mode) {
                this.set_niche_mode(false);
                this.call('end_nichee');
                result = true;
            } else if (this._handler && this._handler.is_creating()) {
                this.clear_selection();
                result = true;
            } else if (!this._handler && this._tmp_walls && this._tmp_walls.length) {
                this.remove_last_wall();
                this.move(ev);
                result = true;
            }
        }
        return result;
    }

    //***********************************************************************************
    //**** remove last wall
    //***********************************************************************************
    remove_last_wall() {
        console.log("removing last wall");
        this._tmp_walls.splice(this._tmp_walls.length - 1, 1);
        if (this._tmp_walls.length == 0)
            this._tmp_vertices = [];
        else
            this._tmp_vertices.splice(this._tmp_vertices.length - 1, 1);
    }

    //***********************************************************************************
    //**** Initiate a contour
    //***********************************************************************************
    initiate_contour() {
        this.is_transient = true;
        var v1 = new cn_vertex(this._mouseover_vertex.position, false, this._scene);
        this._tmp_vertices.push(v1);
        const ww = new cn_wall(this._mouseover_vertex, v1, this._current_wall_type, this._current_axis, this._scene);
        this._tmp_walls.push(ww);
        this._terminate_edition();
    }

    //***********************************************************************************
    //**** Resume contour
    //***********************************************************************************
    resume_contour() {
        this.is_transient = true;
        var v1 = new cn_vertex(this._mouseover_vertex.position, false, this._scene);
        this._tmp_vertices.push(v1);
        this._tmp_walls.push(new cn_wall(this._mouseover_vertex, v1, this._tmp_walls[0].wall_type, this._tmp_walls[0].axis, this._scene));

        while (this._wall_measures.length < this._tmp_walls.length)
            this._wall_measures.push(new cn_tmp_wall_measure(this, this._tmp_walls[this._wall_measures.length]));
        this._wall_measures[this._tmp_walls.length - 2].fix_wall(this._max_distance);
    }

    //***********************************************************************************
    //**** Close contour
    //***********************************************************************************
    close_contour(force_end = false) {
        this.is_transient = false;

        //*** Start transaction now
        this.push_transaction((this._tmp_walls[0].balcony) ? 'Création de balcon' : 'Création de mur');

        //** If end is forced, we remove the last tmp wall */
        const new_walls = this._tmp_walls;
        if (force_end || this._mouseover_vertex == this._tmp_walls[this._tmp_walls.length - 1].vertices[0])
            new_walls.splice(new_walls.length - 1, 1);
        new_walls.forEach(w => this._scene.insert_wall(w, this._scene.building.transaction_manager));

        //*** finalize */
        this._scene.full_update();
        this.clear_selection();
        this.call('creation');
        this._initiate_edition(new_walls);
    }

    //***********************************************************************************
    //**** update contour
    //***********************************************************************************
    set_vertex_position(vertex, position) {
        vertex.position = position;
        this._map.refresh_tool();
        return true;
    }

    /**
     * Adjust a tmp wall where one measure is fixed.
     * Input can be :
     * - Either one point that belongs to the ucoming wall axis
     * - Either the direction of the upcoming wall
     * @param {cn_tmp_wall_measure} wall_measure
     * @param {number} vertex_index
     * @param {number[]} point
     * @param {number[]} direction
     */
    adjust_vertex(wall_measure, vertex_index, point = null, direction = null) {
        //*** Initiate vertex position to original position */
        wall_measure._wall.vertices[vertex_index].position = wall_measure._original_positions[vertex_index];

        //*** Nothing more to do if no constraint */
        if (!point && !direction) return true;
        if (wall_measure._lock < 0) return true;

        //*** check which side is locked */
        const l0 = (wall_measure._lock == 1 - vertex_index);

        //*** Compute direction (that ends on interest vertex) */
        const D = (vertex_index == 1) ? wall_measure._wall.bounds.direction : cn_mul(wall_measure._wall.bounds.direction, -1);
        if (direction && !(Math.abs(cn_dot(direction, D)) < CN_WALL_COSINE_SHAPE_THRESHOLD)) return true;
        const N = cn_normal(D);

        //*** V1 is the original position of the vertex */
        const V1 = wall_measure._original_positions[vertex_index];

        //*** y gives hint of the side of the wall the adjustment is to be done */
        const y = (point) ? cn_dot(N, cn_sub(point, V1)) : cn_dot(N, direction);
        const ymin = (vertex_index == 1) ? wall_measure._wall.bounds.y0 : -wall_measure._wall.bounds.y1;
        const ymax = (vertex_index == 1) ? wall_measure._wall.bounds.y1 : -wall_measure._wall.bounds.y0;

        //*** upcoming wall is nearly parallel to the locked wall. We cannot compute */
        if (point && y <= ymax && y >= ymin) return false;

        //*** s0 is true if lower side is seen by the new point */
        const s0 = (y < 0);

        //*** e is the distance to be added to the fixed point of the measure, orthognally to the upcoming wall */
        var e = (s0) ? -ymin : ymax;
        if (s0 != l0) e = ymax - ymin - e;

        //*** if e is 0, (the locked side is the wall axis) no adjustment needs to be done */
        if (e == 0) {
            wall_measure._wall.vertices[vertex_index].position = V1;
            return true;
        }

        //*** P0 is the end of the locked side measure. We must transform the wall so that this point doesn't move */
        const P0 = cn_add(V1, cn_mul(N, l0 ? ymin : ymax));

        //*** if point is defined, compute wall direction */
        if (point) {
            //*** dx, dy is an axis system that starts at P0. */
            const dx = cn_sub(P0, point);
            cn_normalize(dx);
            var dy = cn_normal(dx);
            if ((cn_dot(dy, D) < 0) == (s0 == l0)) dy = cn_mul(dy, -1);

            //*** a little trigonometry... */
            const sina = e / cn_dist(P0, point);
            const cosa = Math.sqrt(1 - sina * sina);
            const Pp0 = cn_add(P0, cn_mul(dy, e / cosa));
            direction = cn_sub(Pp0, point);
            cn_normalize(direction);
            if (!(Math.abs(cn_dot(direction, D)) < CN_WALL_COSINE_SHAPE_THRESHOLD)) return true;
        }
        //*** if wall direction is defined, compute point */
        else {
            var dy = cn_normal(direction);
            if ((cn_dot(dy, D) < 0) == (s0 == l0)) dy = cn_mul(dy, -1);
            point = cn_add(P0, cn_mul(dy, e));
        }

        //*** new vertex is intersection betwen current locked side and upcoming wall axis */
        const V = cn_intersect_line(point, direction, V1, D);
        if (!V) return false;

        //*** Maybe the offset is not allowed, for there is an intersection with other walls */
        if (vertex_index == 1 && cn_dist(wall_measure._original_positions[0], V) > wall_measure._max_distance) {
            return false;
        }

        wall_measure._wall.vertices[vertex_index].position = V;
        return true;
    }

    //***********************************************************************************
    //**** update contour
    //***********************************************************************************

    _snap(ev, snap) {
        //*** search for a free vertex */
        const obj = this;
        function vertex_filter(v) {
            if (!v.is_free()) return false;
            if (v.walls.length != 1) return false;
            if (v.walls[0].wall_type.constructor != obj._current_wall_type.constructor) return false;
            if (v.walls[0].wall_type.free != obj._current_wall_type.free) return false;
            return true;
        }
        snap.vertex = this._scene.vertices.filter(v => vertex_filter(v)).find(v => cn_dist(v.position, snap.position) < snap.range);
        if (snap.vertex) {
            snap.freedoms = 0;
            return;
        }

        //*** Snap on the first vertex */
        if (this._tmp_walls.length > 2 &&
            this._tmp_vertices[0] == this._tmp_walls[0].vertices[0] &&
            this._tmp_vertices[0].is_free() &&
            cn_dist(this._tmp_vertices[0].position, snap.position) < snap.range) {
            snap.vertex = this._tmp_vertices[0];
            snap.freedoms = 0;
            return;
        }

        //*** Snap on the previous vertex (to close the contour) */
        if (snap.previous_point && cn_dist(snap.previous_point, snap.position) < snap.range) {
            snap.vertex = this._tmp_vertices[this._tmp_vertices.length - 2];
            snap.freedoms = 0;
            return;
        }

        //*** Snap on wall under the mouse
        let support_wall = null;
        if (snap.previous_point) {
            const dir = cn_sub(snap.position, snap.previous_point);
            const max_distance = cn_normalize(dir) + snap.range;
            const shape_impact = this._scene.raytrace_support_shape(snap.previous_point, dir, max_distance, this._tmp_walls[this._tmp_walls.length - 1].vertices[0]);
            if (shape_impact && shape_impact.distance >= max_distance - 2 * snap.range)
                support_wall = shape_impact.wall;
        }
        if (!support_wall)
            support_wall = this._scene.find_wall(ev.mouse_world, ev.camera.snap_world_distance);
        if (support_wall) {
            snap.find_wall_support(support_wall);
            if (snap.freedoms == 0) return;
        }

        //*** If this is the first point, only possible snap is on the grid */
        if (!snap.previous_point) {
            snap.snap_point();
            snap.check_grid();
            return;
        }

        //***Snap on previous walls */
        for (var n = 0; n < this._tmp_walls.length - 2; n++) {
            snap.find_wall_support(this._tmp_walls[n]);
            if (snap.freedoms == 0) return;
        }

        //*** snap angle on previous wall */
        let previous_wall = null;
        if (this._tmp_walls.length == 1) {
            //*** Maybe we are starting on a new vertex */
            if (this._tmp_walls[0].vertices[0] == this._tmp_vertices[0])
                previous_wall = this._tmp_vertices[0].support_wall;
            //*** Maybe we are starting on an existing vertex */
            else if (this._tmp_walls[0].vertices[0].walls.length)
                previous_wall = this._tmp_walls[0].vertices[0].walls[0];
        }
        else
            previous_wall = this._tmp_walls[this._tmp_walls.length - 2];
        if (previous_wall) {
            snap.check_angles(previous_wall.vertex_position(0), previous_wall.vertex_position(1), 45);
            if (snap.freedoms == 0) return;
        }

        //*** Snap perpendicular to neighboring walls */
        if (this._tmp_walls.length > 0 && snap.freedoms == 1) {
            const dir = cn_sub(snap.position, snap.previous_point);
            cn_normalize(dir);
            const axis = this._tmp_walls[this._tmp_walls.length - 1].axis;
            const thickness = this._tmp_walls[this._tmp_walls.length - 1].wall_type.thickness;
            var dirt = cn_mul(dir, thickness);
            var dir_off = cn_mul(dir, snap.range);
            cn_normalize(dir);
            if (Math.abs(cn_dot(dir, snap.direction)) > 0.99) {
                const normal = cn_normal(dir);

                for (var side = 0; side < 2; side++) {
                    cn_flip(normal);
                    var p00 = cn_clone(snap.position);
                    var x0 = 0;
                    if (axis == 0)
                        x0 = -0.5;
                    else if (axis == CN_OUTER) {
                        if (side == 0) x0--;
                    } else if (axis == CN_INNER) {
                        if (side == 1) x0--;
                    }
                    var x1 = x0 + 1;
                    const p0 = cn_add(p00, cn_mul(dirt, x0));
                    const p1 = cn_add(p0, dirt);
                    const i0 = this._scene.raytrace_walls(cn_sub(p1, dir_off), normal, 10);
                    const i1 = this._scene.raytrace_walls(cn_add(p1, dir_off), normal, 10);
                    if (!i0 && !i1) continue;
                    var wall = null;
                    if (i0 && i1) {
                        if (i0.wall == i1.wall) continue;
                        wall = (i0.distance < i1.distance) ? i0.wall : i1.wall;
                    } else if (i0)
                        wall = i0.wall;
                    else
                        wall = i1.wall;

                    const index = (cn_dot(dir, wall.bounds.direction) < 0) ? 0 : 1;
                    const wside = (cn_dot(normal, wall.bounds.normal) < 0) ? 1 : 0;
                    const pw = wall.shape[(wside == 0) ? 3 * index : 1 + index];

                    const snap_direction = (snap.freedoms == 2) ? dir : snap.direction;
                    const prj = cn_intersect_line(snap.position, snap_direction, pw, normal);
                    if (prj) {
                        const xx = (i0 && wall == i0.wall) ? x1 : x0;
                        const new_pos = cn_sub(prj, cn_mul(dirt, xx));
                        if (cn_dist(new_pos, snap.start_position) < snap.range) {
                            snap.freedoms--;
                            snap.position = new_pos;
                            snap.direction = normal;
                            snap._add_snap_line(prj, pw);
                            if (xx == x1)
                                snap._add_snap_line(cn_sub(prj, dirt), cn_sub(pw, dirt));
                            else
                                snap._add_snap_line(cn_add(prj, dirt), cn_add(pw, dirt));
                            if (snap.freedoms == 0) break;
                        }
                    }
                }
            }
        }

        //*** Snap on background */
        if (snap.freedoms > 0) {
            snap.snap_point();
        }

        //*** snap on grid
        if (snap.freedoms > 0) {
            snap.check_grid();
        }

        //*** snap on verticals / horizontals */
        if (snap.previous_point) {
            snap.check_line(snap.previous_point, [1, 0]);
            snap.check_line(snap.previous_point, [0, 1]);
        }
    }

    _find_snap_vertex(ev) {

        const nb_walls = this._tmp_walls.length;
        //*** Create snap */
        var active_wall = (nb_walls) ? this._tmp_walls[nb_walls - 1] : null;
        var previous_point = (active_wall) ? active_wall.vertex_position(0) : null;
        var range = ev.camera.snap_world_distance;
        var snap = new cn_snap(ev.mouse_world, range, previous_point, ev.camera);
        snap.draw_svg_angles = false;
        snap.thickness = this._current_wall_type.thickness;
        snap.axis = this._current_axis;
        snap.snap_elements = this._scene.storey.background_maps;
        if (nb_walls) {
            snap.thickness = this._tmp_walls[0].wall_type.thickness;
            snap.axis = this._tmp_walls[0].axis;
        }

        //*** Special case if the measure of the previous wall is locked. We work as if axis had changed */
        const locked_measure = (nb_walls >= 2 && this._wall_measures[nb_walls - 2]._lock >= 0) ? this._wall_measures[nb_walls - 2] : null;
        if (locked_measure) {
            snap.previous_point = cn_add(locked_measure._measures[locked_measure._lock].vertices[locked_measure._lock], cn_mul(this._tmp_walls[nb_walls - 2].bounds.direction, locked_measure._locked_value));
            snap.axis = (locked_measure._lock == 0) ? CN_INNER : CN_OUTER;
        }

        //*** Perform snap */
        this._snap(ev, snap);

        //*** We check the snapped position */
        if (nb_walls > 0) {
            const new_dir = cn_sub(snap.position, snap.previous_point);
            cn_normalize(new_dir);

            //*** We don't accept to go backward */
            if (nb_walls >= 2) {
                if (cn_dot(new_dir, this._tmp_walls[nb_walls - 2].bounds.direction) < -CN_WALL_COSINE_SHAPE_THRESHOLD)
                    return null;
            }

            //*** don't be parallel to the support wall */
            /*if (nb_walls == 1) {
                if (this._tmp_walls[0].vertices[0].support_wall) {
                    if (Math.abs(cn_dot(new_dir, this._tmp_walls[0].vertices[0].support_wall.bounds.direction)) >= CN_WALL_COSINE_SHAPE_THRESHOLD)
                        return null;
                }
                if (this._tmp_walls[0].vertices[0] != this._tmp_vertices[0]) {
                    const vtx = this._tmp_walls[0].vertices[0];
                    if (vtx.walls.length == 1) {
                        if (cn_dot(new_dir, vtx.walls[0].direction_from(vtx)) >= CN_WALL_COSINE_SHAPE_THRESHOLD)
                            return null;
                    }
                }
            }*/
        }

        //*** If previous measure is locked, we have to fix vertices */
        if (locked_measure && snap.axis != this._tmp_walls[0].axis) {
            let x = 0;
            if (locked_measure._lock) x -= snap.thickness;
            if (this._tmp_walls[0].axis == CN_OUTER) x += snap.thickness;
            if (this._tmp_walls[0].axis == CN_MIDDLE) x += snap.thickness * 0.5;
            let wall_normal = cn_normal(cn_sub(snap.position, snap.previous_point));
            cn_normalize(wall_normal);

            if (snap.wall) {
                const segment = snap.wall.get_side_segment(snap.wall_side);
                const dir = cn_sub(segment[1], segment[0]);
                cn_normalize(dir);
                x /= Math.abs(cn_dot(dir, wall_normal));
                snap.position = cn_sub(snap.position, cn_mul(dir, x));
            }
            else if (snap.vertex) {
                let wall_dir = cn_polar(cn_sub(snap.position, snap.previous_point));
                wall_dir[1] += Math.asin(x / wall_dir[1]) + Math.PI / 2;
                wall_dir[0] = 1;
                wall_normal = cn_cart(wall_dir);
            }
            else {
                snap.position = cn_add(snap.position, cn_mul(wall_normal, x));
            }

            const previous_wall = this._tmp_walls[nb_walls - 2];
            const median = cn_add(previous_wall.bounds.normal, wall_normal);
            cn_normalize(median);
            const p = cn_intersect_line(previous_wall.vertices[1].position, previous_wall.bounds.direction, snap.previous_point, median);
            if (p) previous_wall.vertices[1].position = p;
            snap.axis = this._tmp_walls[0].axis;
        }

        //*** special snap visual for first point, if supported */
        this._extra_measures = [];
        if (nb_walls == 0 && snap.wall) {
            const segment = snap.wall.get_side_segment(snap.wall_side);
            const dir = cn_sub(segment[1], segment[0]);
            cn_normalize(dir);
            const normal = cn_mul(cn_normal(dir), -1);
            let xmin = 0;
            if (snap.axis == CN_OUTER) xmin -= snap.thickness;
            if (snap.axis == CN_MIDDLE) xmin -= snap.thickness * 0.5;
            const p0 = cn_add(snap.position, cn_mul(dir, xmin));
            const p1 = cn_add(snap.position, cn_mul(dir, xmin + snap.thickness));
            const pp0 = ev.camera.world_to_screen(p0);
            let pp = ev.camera.world_to_screen(cn_add(p0, cn_mul(normal, 0.5)));
            this._snap_svg = `<line class="snap_wall" x1="${pp0[0]}" y1="${pp0[1]}" x2="${pp[0]}" y2="${pp[1]}" />`;
            const pp1 = ev.camera.world_to_screen(p1);
            this._snap_svg += `<line class="snap_wall" x1="${pp0[0]}" y1="${pp0[1]}" x2="${pp1[0]}" y2="${pp1[1]}" />`;
            pp = ev.camera.world_to_screen(cn_add(p1, cn_mul(normal, 0.5)));
            this._snap_svg += `<line class="snap_wall" x1="${pp1[0]}" y1="${pp1[1]}" x2="${pp[0]}" y2="${pp[1]}" />`;

            this._extra_measures = [new cn_measure(this, segment[0], p0, false), new cn_measure(this, p1, segment[1], false)];
        }
        else {
            this._snap_svg = snap.svg;
        }

        //*** Maybe we found an existing vertex */
        if (snap.vertex) return snap.vertex;

        const vertex = this._tmp_vertices[this._tmp_vertices.length - 1];
        vertex.position = snap.position;
        vertex.support_wall = snap.wall;
        vertex.support_wall_side = (snap.wall) ? snap.wall_side : -1;
        return vertex;
    }

    update_contour(ev, dragging) {
        this.is_transient = true;
        if (this._current_wall_type == null) return false;
        this._snap_svg = '';
        this._can_close_contour = false;
        this._mouseover_vertex = null;
        this._check_creation.mouseover = false;
        this._check_creation.mouseover = false;

        /** Check if we are over creation or cancel checkbox */
        if (!dragging) {
            if (this._check_creation.visible) {
                this._check_creation.mouseover = this._check_creation.contains(ev.mouse_world, ev.camera);
                if (this._check_creation.mouseover) {
                    return true;
                }
            }
            if (this._undo_last_wall.visible) {
                this._undo_last_wall.mouseover = this._undo_last_wall.contains(ev.mouse_world, ev.camera);
                if (this._undo_last_wall.mouseover) {
                    return true;
                }
            }
        }

        //*** Find matching snap vertex */
        this._mouseover_vertex = this._find_snap_vertex(ev);
        if (!this._mouseover_vertex) return true;

        //*** set current wall */
        if (this._tmp_walls.length)
            this._tmp_walls[this._tmp_walls.length - 1].vertices[1] = this._mouseover_vertex;

        //** We can close contour if current vertex is supported, or duplicate. */
        this._mouseover_previous_point = (this._tmp_walls.length > 0 && this._mouseover_vertex == this._tmp_walls[this._tmp_walls.length - 1].vertices[0]);
        this._can_close_contour = !this._mouseover_previous_point && (this._mouseover_vertex.support_wall || this._mouseover_vertex != this._tmp_vertices[this._tmp_vertices.length - 1]);
        return true;
    }

    //***********************************************************************************
    //**** Split a wall
    //***********************************************************************************
    /**
     * Check what can be splitted.
     * @param {*} ev
     * @returns
     */
    update_split(ev) {
        this._split_position = null;
        this._split_wall = null;

        //*** are we on a splittable vertex ?  */
        this._split_vertex = this._scene.vertices.filter(v => v.walls.length == 2).find(v => cn_dist(ev.mouse_world, v.position) < ev.camera.snap_world_distance);
        if (this._split_vertex) {
            this._split_position = this._split_vertex.position;
            return true;
        }

        //*** Are we on a wall? */
        this._split_wall = this._scene.find_wall(ev.mouse_world, ev.camera.snap_world_distance);
        if (this._split_wall) {
            const a = this._split_wall.compute_abcissa(ev.mouse_world);
            if (a > 0.1 && a < this._split_wall.bounds.length - 0.1) {
                this._split_position = this._split_wall.compute_axis_position_from_abcissa(a);
            }
        }
    }

    /**
     * Actually splits a wall or a vertex.
     * @returns
     */
    split_wall(ev) {
        if (!this._split_position) return;
        if (!this._split_vertex && !this._split_wall) return;

        //*** Split a vertex */
        if (this._split_vertex) {
            if (this._split_vertex.walls.length != 2) return;
            this.push_transaction("Scission de sommet");
            this._split_vertex.walls.forEach((w, k) => {
                const dir = w.direction_from(this._split_vertex);
                const new_pos = cn_add(this._split_vertex.position, cn_mul(dir, 0.1));
                if (k == 0) {
                    const new_vertex = new cn_vertex(new_pos, false, this._scene);
                    this.push_item_set(this._scene, "vertices");
                    this._scene.vertices.push(new_vertex);
                    this.push_item_set(w, "vertices");
                    const vi = w.vertices.indexOf(this._split_vertex);
                    w.vertices[vi] = new_vertex;
                }
                else {
                    this.push_item_set(this._split_vertex, "position");
                    this._split_vertex.position = new_pos;
                }

            });
        }

        //*** Split a wall */
        else {
            this.push_transaction('Découpe de mur');
            const new_vertex = new cn_vertex(this._split_position, false, this._scene);
            this._scene.split_wall(this._split_wall, new_vertex, this._scene.building.transaction_manager);
        }

        this._scene.full_update();
    }

    //***********************************************************************************
    //**** Niche creation
    //***********************************************************************************

    update_niche(ev) {

        let wall = this._scene.find_wall(ev.mouse_world, ev.camera.snap_world_distance);
        if (wall) {
            let wp = this._niche.wall_position;
            let side = this._niche.wall_side;
            let old_wall = this._niche.wall;
            this._niche.wall_position = wall.compute_abcissa(ev.mouse_world) - 0.5 * this._niche.width;
            this._niche.wall_side = wall.compute_side(ev.mouse_world);
            this._niche.wall = wall;
            this._niche.update_wall_position();
            wall.niches.push(this._niche);
            wall.update_niches();
            if (!this._niche.valid)
            {
                this._niche.wall_position = wp;
                this._niche.wall_side = side;
                this._niche.wall = old_wall;
            }
            let index = wall.niches.indexOf(this._niche);
            wall.niches.splice(index,1);
        }
    }
    
    create_niche() {
        if (this._niche.wall)
        {
            this.push_transaction("Création de niche");
            let new_niche = this._niche;
            this.push_item_set(new_niche.wall,"niches");
            new_niche.wall.niches.push(new_niche);
            this._niche = new cn_niche(this._building.get_niche_types()[0],this._scene);
            this.call("creation");
            this._initiate_edition([new_niche]);
        }
    }
    //***********************************************************************************
    //**** Find mouseover vertex
    //***********************************************************************************

    find_mouseover_vertex(camera, mouse) {
        var vertices = this._scene.vertices.concat(this._tmp_vertices);
        if (this._tmp_vertices.length > 0)
            vertices.splice(vertices.length - 1, 1);
        return super.find_mouseover_vertex(camera, mouse, vertices);
    }

    //***********************************************************************************
    //**** Find mouseover wall
    //***********************************************************************************

    find_mouseover_wall(camera, mouse) {
        var walls = this._scene.walls.concat(this._tmp_walls);
        if (this._tmp_walls.length > 0)
            walls.splice(walls.length - 1, 1);
        return super.find_mouseover_wall(camera, mouse, walls);
    }

    //***********************************************************************************
    /**
     * Initiate creation by rectangle.
     */
    initiate_rectangle() {
        var obj = this;
        var scene = obj._scene;
        this._handler = cn_polygon_handler.create_rectangle(this, 5);
        this._handler.accept_outside = true;
        this._handler.snap_elements = scene.walls;
        this._handler.display_measures = false;

        const prec = obj._current_wall_type.thickness;

        //*** Event on creation start */
        this._handler.on('start_creation', () => {
            obj._terminate_edition();
        });

        //*** reorder the list of vertices until first vertex is not on awall */
        function reorder_list(vertices_org) {
            var vertices = vertices_org.concat([]);
            const f = vertices[0];
            vertices.splice(0, 1);
            vertices.push(f);
            vertices.reverse();
            for (var i = 0; i < vertices.length; i++) {
                if (scene.find_wall(vertices[0], prec) == null) return vertices;
                var vts = vertices[0];
                vertices.splice(0, 1);
                vertices.push(vts);
            }
            return null;
        }

        //***************************************************************/
        /** Method to cut rectangle walls
         * This method assumes that the first point does not lie on an existing wall.
         * It will build tmp walls and vertices until either loop to the first point, either blocked by an existing wall.
         * If blocked, return value will be 'false' so that the same function is called once again in the other sense.
         *
         *                          V0---------------V1
         *                           |                |
         *                           |                |
         *                           |                x
         *                           |
         *                           |
         *                           V3-------x       V2
         *
         * Example above :
         * - First call on [v0,v1,v2,v3] -> blocked between v1 and v2
         * - Second call on [v0,v3,v2,v1] -> blocked between v3 and v2.
         *
         * If not blocked, one single call on [v0,v1,v2,v3]
         *
         */
        function follow_path(vertices, reverse = false, perform = false) {

            function add_wall(v0, v1) {
                var wall = (reverse) ? new cn_wall(v1, v0, obj._current_wall_type, obj._current_axis, obj._scene) : new cn_wall(v0, v1, obj._current_wall_type, obj._current_axis, obj._scene);
                obj._tmp_walls.push(wall);
            }

            //*** First vertex is either a new one, eihter the first vertex (second call to 'follow_path') */
            let start_vertex = null;
            if (obj._tmp_vertices.length > 0) {
                start_vertex = obj._tmp_vertices[0];
            } else {
                start_vertex = new cn_vertex(vertices[0], false, scene);
                obj._tmp_vertices.push(start_vertex);
            }

            //*** Loop on vertices */
            var last_vertex = start_vertex;
            for (var index = 0; index < vertices.length; index++) {
                //*** next point geometry */
                var next_point = (index < vertices.length - 1) ? vertices[index + 1] : vertices[0];
                var dir = cn_sub(next_point, last_vertex.position);
                var length = cn_normalize(dir);

                //*** Is there an impact along the ray ? */
                var impact = scene.raytrace_spaces(last_vertex.position, dir, length);
                var ww = null;
                var e = 0;

                if (impact && impact.space_segment.wall) {
                    const ss = impact.space_segment;
                    ww = impact.space_segment.wall;

                    const snap = new cn_snap(impact.point, obj._camera.snap_world_distance, null, obj._camera);
                    snap.axis = (reverse) ? cn_reverse_axis(obj._current_axis) : obj._current_axis;
                    snap.thickness = obj._current_wall_type.thickness;
                    snap.find_wall_support(ww);

                    var next_vertex = new cn_vertex(snap.position, false, scene);
                    next_vertex.support_wall = ww;
                    add_wall(last_vertex, next_vertex);

                    //*** Snap on last position */
                    if (Math.abs(dir[0]) < Math.abs(dir[1]))
                        last_vertex.position[0] = next_vertex.position[0];
                    else
                        last_vertex.position[1] = next_vertex.position[1];

                    return false;
                }

                //*** next point has no obstacle */
                // @ts-ignore
                var next_vertex = (index == vertices.length - 1) ? start_vertex : new cn_vertex(vertices[index + 1], false, scene);
                add_wall(last_vertex, next_vertex);
                if (index < vertices.length - 1) {
                    obj._tmp_vertices.push(next_vertex);
                    last_vertex = next_vertex;
                } else
                    return true;
            }
            return true;
        }

        function check_path(vertices, perform) {
            var old_vertices = obj._tmp_vertices;
            var old_walls = obj._tmp_walls;

            obj._tmp_vertices = [];
            obj._tmp_walls = [];
            if (!follow_path(vertices, false, false)) {
                if (vertices.length == 4)
                    follow_path([vertices[0], vertices[3], vertices[2], vertices[1]], true, false);
                else
                    follow_path([vertices[0], vertices[4], vertices[3], vertices[2], vertices[1]], true, false);
            }

            //*** If any wall is not consequent, reset and return false */
            if (obj._tmp_walls.some(wall => cn_dist(wall.vertices[0].position, wall.vertices[1].position) < 0.1)) {
                obj._tmp_vertices = old_vertices;
                obj._tmp_walls = old_walls;
                return false;
            }
            if (perform) {
                obj._tmp_vertices = [];
                obj._tmp_walls = [];
                if (!follow_path(vertices, false, true)) {
                    if (vertices.length == 4)
                        follow_path([vertices[0], vertices[3], vertices[2], vertices[1]], true, true);
                    else
                        follow_path([vertices[0], vertices[4], vertices[3], vertices[2], vertices[1]], true, true);
                }
            }
            return true;
        }

        //***************************************************************/
        //*** Callback upon wall creation */
        this._handler.on_change = () => {

            //*** Reorder vertex list to have the first vertex not on an existing wall */
            var vertices = reorder_list(obj._handler.vertices);
            if (vertices == null) {
                obj.initiate_rectangle();
                return;
            }

            //*** Undo redo management */
            const transaction_name = (obj._current_wall_type.balcony) ? 'Création de balcons' : 'Création de murs';
            obj.push_transaction(transaction_name);

            //*** Compute new wall */
            check_path(vertices, true);

            const new_walls = obj._tmp_walls;
            new_walls.forEach(w => scene.insert_wall(w, scene.building.transaction_manager));
            obj._tmp_vertices = [];
            obj._tmp_walls = [];

            //*** Rebuild spaces */
            scene.full_update();

            //*** New rectancular wall */
            obj.initiate_rectangle();
            obj.call('creation');
            obj._initiate_edition(new_walls);
        };

        //***************************************************************/
        //*** Callback upon wall drawing */
        this._handler.check_change = () => {

            //*** Reorder vertex list to have the first vertex not on an existing wall */
            var vertices = reorder_list(obj._handler.vertices);
            if (vertices == null) return false;

            //*** Update tmp walls and vertices */
            check_path(vertices, false);

            return true;
        };
    }

    /**
     * TODO : derivate in order to provide an edition handler
     * @param {Array<cn_element>} elements
     * @returns {cn_edition_handler}
     */
    _build_edition_handler(elements) {
        if (!elements.length) return null;
        if (elements[0].constructor == cn_niche)
            // @ts-ignore
            return new cn_niche_handler(elements, this._map, true);
        // @ts-ignore
        return new cn_wall_handler(elements, this._map, true);
    }

    /**
     * TODO : derivate in order to find siblings of an element
     * @param {cn_wall} element
     * @returns {Array<cn_element>}
     */
    _get_siblings(element) {
        const ot = element.wall_type;
        return this._scene.walls.filter(op => op.wall_type == ot);
    }
}

